/**
 * SearchBar - Unified search component
 * Always-visible search bar for the top navigation
 * Supports: stock search, page navigation, AI queries (with ? prefix)
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Search,
  TrendingUp,
  FileText,
  Sparkles,
  ArrowRight,
  Clock,
  X,
  Loader2,
  Command,
  Filter,
  Mic,
  MicOff
} from 'lucide-react'
import clsx from 'clsx'
import { apiClient } from '../api/client'
import { nlScreenerApi } from '../api/screener'
import { useUIStore } from '../stores/uiStore'
import { useVoiceInput } from '../hooks/useVoiceInput'
import './SearchBar.css'

interface SearchResult {
  ticker: string
  company_name: string
  sector?: string
  current_price?: number
  change_pct?: number
}

interface PageResult {
  path: string
  name: string
  category: string
  keywords?: string[]
}

// Common tickers for instant results
const COMMON_TICKERS: Record<string, string> = {
  'AAPL': 'Apple Inc.',
  'MSFT': 'Microsoft Corporation',
  'GOOGL': 'Alphabet Inc.',
  'AMZN': 'Amazon.com Inc.',
  'NVDA': 'NVIDIA Corporation',
  'TSLA': 'Tesla Inc.',
  'META': 'Meta Platforms Inc.',
  'JPM': 'JPMorgan Chase & Co.',
  'SPY': 'S&P 500 ETF Trust',
  'QQQ': 'Invesco QQQ Trust',
}

const PAGES: PageResult[] = [
  { path: '/', name: 'Dashboard', category: 'Pages', keywords: ['dashboard', 'home'] },
  { path: '/markets', name: 'Markets', category: 'Pages', keywords: ['markets', 'stocks', 'equities'] },
  { path: '/markets/equities', name: 'Equities', category: 'Markets', keywords: ['equities', 'stocks', 'macro'] },
  { path: '/markets/etfs', name: 'ETFs', category: 'Markets', keywords: ['etf', 'funds', 'index'] },
  { path: '/markets/fixed-income', name: 'Fixed Income', category: 'Markets', keywords: ['bonds', 'treasury', 'fixed'] },
  { path: '/markets/dividends', name: 'Dividends', category: 'Markets', keywords: ['dividend', 'income', 'yield'] },
  { path: '/markets/internals', name: 'Market Internals', category: 'Markets', keywords: ['internals', 'breadth'] },
  { path: '/research', name: 'Research', category: 'Pages', keywords: ['research', 'stock'] },
  { path: '/portfolio', name: 'Portfolio', category: 'Pages', keywords: ['portfolio', 'positions', 'holdings'] },
  { path: '/screener', name: 'Screener', category: 'Pages', keywords: ['screener', 'filter', 'scan'] },
  { path: '/strategy', name: 'Strategy', category: 'Pages', keywords: ['strategy', 'backtest'] },
  { path: '/intelligence', name: 'Intelligence', category: 'Pages', keywords: ['intelligence', 'ai', 'insights'] },
  { path: '/intelligence/brief', name: 'Daily Brief', category: 'Intelligence', keywords: ['brief', 'daily'] },
  { path: '/intelligence/analysis', name: 'AI Analysis', category: 'Intelligence', keywords: ['ai', 'analysis', 'chat'] },
  { path: '/intelligence/news', name: 'News Impact', category: 'Intelligence', keywords: ['news', 'impact'] },
  { path: '/alerts', name: 'Alerts', category: 'Pages', keywords: ['alerts', 'notifications'] },
  { path: '/watchlist', name: 'Watchlist', category: 'Pages', keywords: ['watchlist', 'watch', 'track'] },
  { path: '/settings', name: 'Settings', category: 'Pages', keywords: ['settings', 'preferences'] },
]

const RECENT_SEARCHES_KEY = 'cipher_recent_searches'
const MAX_RECENT = 5

export function SearchBar() {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { sendAIPrompt, openCommandPalette } = useUIStore()

  // Voice input (Prompt 14)
  const {
    isListening,
    isSupported: isVoiceSupported,
    transcript: voiceTranscript,
    interimTranscript,
    toggleListening,
    clearTranscript
  } = useVoiceInput({ silenceTimeout: 4000 })

  // Load recent searches
  useEffect(() => {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored))
      } catch {
        // Ignore invalid JSON
      }
    }
  }, [])

  // Handle click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Voice input: update query with interim transcript (Prompt 14)
  useEffect(() => {
    if (isListening && interimTranscript) {
      setQuery(interimTranscript)
      setIsOpen(true)
    }
  }, [isListening, interimTranscript])

  // Voice input: auto-search when final transcript is ready (Prompt 14)
  useEffect(() => {
    if (voiceTranscript && !isListening) {
      setQuery(voiceTranscript)
      setIsOpen(true)
      // Auto-execute first result after a brief delay
      setTimeout(() => {
        if (allResults.length > 0) {
          handleSelect(0)
        }
      }, 300)
      clearTranscript()
    }
  }, [voiceTranscript, isListening, clearTranscript])

  // Search stocks API
  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['searchbar-search', query],
    queryFn: async () => {
      if (query.length < 2 || query.startsWith('?')) return []
      const response = await apiClient.get(`/search?q=${encodeURIComponent(query)}`)
      return response.data.results as SearchResult[]
    },
    enabled: query.length >= 2 && !query.startsWith('?') && isOpen,
    staleTime: 30000
  })

  // Detect screener-like queries (Prompt 13)
  const { data: screenerDetection } = useQuery({
    queryKey: ['searchbar-screener-detect', query],
    queryFn: () => nlScreenerApi.detectScreenerQuery(query),
    enabled: query.length >= 8 && !query.startsWith('?') && isOpen,
    staleTime: 60000
  })

  const isScreenerQuery = screenerDetection?.is_screener_query && screenerDetection.confidence > 0.5

  const isAIQuery = query.startsWith('?')
  const aiQueryText = isAIQuery ? query.slice(1).trim() : ''

  // Filter pages by query
  const filteredPages = useMemo(() => {
    if (query.length === 0 || isAIQuery) return []
    const q = query.toLowerCase()
    return PAGES.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.path.toLowerCase().includes(q) ||
      p.keywords?.some(kw => kw.includes(q))
    ).slice(0, 4)
  }, [query, isAIQuery])

  // Filter common tickers
  const matchedTickers = useMemo(() => {
    if (query.length === 0 || isAIQuery) return []
    const q = query.toUpperCase()
    const matches: { ticker: string; name: string }[] = []

    for (const [ticker, name] of Object.entries(COMMON_TICKERS)) {
      if (ticker.includes(q) || name.toUpperCase().includes(q)) {
        matches.push({ ticker, name })
      }
      if (matches.length >= 5) break
    }

    return matches
  }, [query, isAIQuery])

  // Combine results
  const tickerResults = useMemo(() => {
    const results: SearchResult[] = []
    const seen = new Set<string>()

    for (const { ticker, name } of matchedTickers) {
      if (!seen.has(ticker)) {
        results.push({ ticker, company_name: name })
        seen.add(ticker)
      }
    }

    if (searchResults) {
      for (const result of searchResults) {
        if (!seen.has(result.ticker)) {
          results.push(result)
          seen.add(result.ticker)
        }
      }
    }

    return results.slice(0, 6)
  }, [matchedTickers, searchResults])

  // All results combined
  const allResults = useMemo(() => {
    const results: Array<{
      type: 'ai' | 'page' | 'stock' | 'recent' | 'action' | 'screener'
      label: string
      sublabel?: string
      path?: string
      action?: () => void
    }> = []

    // AI query
    if (isAIQuery && aiQueryText.length > 0) {
      results.push({
        type: 'ai',
        label: `Ask AI: "${aiQueryText}"`,
        sublabel: 'Send to AI Analysis'
      })
    }

    // Screener query detection (Prompt 13)
    if (isScreenerQuery && query.length >= 8) {
      results.push({
        type: 'screener',
        label: `Screen for: "${query}"`,
        sublabel: `Use AI to find matching stocks (${Math.round((screenerDetection?.confidence || 0) * 100)}% match)`,
        path: `/screener?q=${encodeURIComponent(query)}`
      })
    }

    // Pages
    for (const page of filteredPages) {
      results.push({
        type: 'page',
        label: page.name,
        sublabel: page.category,
        path: page.path
      })
    }

    // Stocks
    for (const stock of tickerResults) {
      results.push({
        type: 'stock',
        label: stock.ticker,
        sublabel: stock.company_name,
        path: `/stock/${stock.ticker}`
      })
    }

    // Recent searches when query is empty
    if (query.length === 0 && recentSearches.length > 0) {
      for (const recent of recentSearches.slice(0, 3)) {
        results.push({
          type: 'recent',
          label: recent,
          sublabel: 'Recent search'
        })
      }
    }

    // Command palette shortcut
    if (query.length === 0) {
      results.push({
        type: 'action',
        label: 'Open Command Palette',
        sublabel: 'Cmd+K for more options',
        action: () => openCommandPalette?.()
      })
    }

    return results
  }, [isAIQuery, aiQueryText, isScreenerQuery, screenerDetection, filteredPages, tickerResults, query, recentSearches, openCommandPalette])

  // Save to recent searches
  const saveToRecent = useCallback((term: string) => {
    const updated = [term, ...recentSearches.filter(r => r !== term)].slice(0, MAX_RECENT)
    setRecentSearches(updated)
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
  }, [recentSearches])

  // Handle selection
  const handleSelect = useCallback((index: number) => {
    const item = allResults[index]
    if (!item) return

    if (item.type === 'ai') {
      sendAIPrompt?.(aiQueryText)
      navigate('/intelligence/analysis')
      saveToRecent(query)
    } else if (item.type === 'action' && item.action) {
      item.action()
    } else if (item.path) {
      navigate(item.path)
      if (item.type === 'stock') {
        saveToRecent(item.label)
      }
    } else if (item.type === 'recent') {
      setQuery(item.label)
      return // Don't close, let user search
    }

    setQuery('')
    setIsOpen(false)
  }, [allResults, aiQueryText, navigate, saveToRecent, sendAIPrompt, query])

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        handleSelect(selectedIndex)
        break
      case 'Escape':
        setIsOpen(false)
        inputRef.current?.blur()
        break
    }
  }, [isOpen, allResults.length, selectedIndex, handleSelect])

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [allResults.length])

  // Get icon for result type
  const getIcon = (type: string) => {
    switch (type) {
      case 'ai':
        return <Sparkles size={16} className="result-icon ai" />
      case 'screener':
        return <Filter size={16} className="result-icon screener" />
      case 'page':
        return <FileText size={16} className="result-icon page" />
      case 'stock':
        return <TrendingUp size={16} className="result-icon stock" />
      case 'recent':
        return <Clock size={16} className="result-icon recent" />
      case 'action':
        return <Command size={16} className="result-icon action" />
      default:
        return <ArrowRight size={16} className="result-icon" />
    }
  }

  return (
    <div className="searchbar-container" ref={containerRef}>
      <div className={clsx('searchbar', isOpen && 'searchbar-focused')}>
        <Search size={18} className="searchbar-icon" />
        <input
          ref={inputRef}
          type="text"
          className="searchbar-input"
          placeholder="Search stocks, pages, or ? for AI..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {query && (
          <button
            className="searchbar-clear"
            onClick={() => {
              setQuery('')
              inputRef.current?.focus()
            }}
          >
            <X size={14} />
          </button>
        )}
        {isLoading && <Loader2 size={16} className="searchbar-loading" />}

        {/* Voice Input Button (Prompt 14) */}
        {isVoiceSupported && (
          <button
            className={clsx('searchbar-mic', isListening && 'listening')}
            onClick={(e) => {
              e.stopPropagation()
              toggleListening()
              if (!isListening) {
                inputRef.current?.focus()
              }
            }}
            aria-label={isListening ? 'Stop voice input' : 'Voice search'}
          >
            {isListening ? <MicOff size={14} /> : <Mic size={14} />}
          </button>
        )}

        <div className="searchbar-shortcut">
          <kbd>⌘</kbd>
          <kbd>K</kbd>
        </div>
      </div>

      {isOpen && (
        <div className="searchbar-dropdown">
          {allResults.length === 0 && query.length > 0 && !isLoading ? (
            <div className="searchbar-empty">
              No results for "{query}"
            </div>
          ) : (
            <div className="searchbar-results">
              {allResults.map((result, index) => (
                <button
                  key={`${result.type}-${result.label}-${index}`}
                  className={clsx('searchbar-result', index === selectedIndex && 'selected')}
                  onClick={() => handleSelect(index)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {getIcon(result.type)}
                  <div className="result-content">
                    <span className="result-label">{result.label}</span>
                    {result.sublabel && (
                      <span className="result-sublabel">{result.sublabel}</span>
                    )}
                  </div>
                  <ArrowRight size={14} className="result-arrow" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchBar
